import { FC, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/context/AuthContext';
import Routes from '@/router/Routes';
import Alert from '@/components/shared/Alert';
import { useTranslation } from 'react-i18next';
import BrandCardSideContent from '@/layouts/BrandedSimple/BrandCardSideContent';

const VerifyInvitePage: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { supabaseClient } = useAuthContext();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    const verifyInvite = async () => {
      try {
        // Check for errors in hash fragments
        const hash = window.location.hash.substring(1); // Remove #
        const hashParams = new URLSearchParams(hash);
        const error = hashParams.get('error');
        const errorDescription = hashParams.get('error_description');
        
        if (error) {
          const friendlyMessage = errorDescription?.replace(/\+/g, ' ') || error;
          
          if (error === 'access_denied' && errorDescription?.includes('expired')) {
            setError('The invitation link has expired. Please request a new invitation.');
          } else {
            setError(friendlyMessage);
          }
          return;
        }
        
        // Check for access_token in hash (magic link format)
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        
        // If we have access token in hash, use setSession
        if (accessToken && refreshToken) {
          const { data: sessionData, error: sessionError } = await supabaseClient.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          
          if (sessionError || !sessionData.session) {
            setError('Failed to establish session. The link may be invalid or expired.');
            return;
          }
          
          // Update user metadata to force password reset
          const { error: updateError } = await supabaseClient.auth.updateUser({
            data: { forceResetPassword: true }
          });

          if (updateError) {
            setError('Failed to update user settings');
            return;
          }

          setIsVerified(true);
          navigate(Routes.RESET_PASSWORD, { replace: true });
          return;
        }
        
        // Fallback to query params for token_hash format
        const tokenHash = searchParams.get('token_hash') || searchParams.get('token');
        const type = searchParams.get('type');

        if (!tokenHash) {
          setError('Invalid verification link. Please use the link from your invitation email.');
          return;
        }

        const { error: verifyError } = await supabaseClient.auth.verifyOtp({
          token_hash: tokenHash,
          type: type === 'invite' ? 'invite' : 'magiclink',
        });

        if (verifyError) {
          setError(verifyError.message);
          return;
        }

        // Wait for session to be established
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Get current session
        const { data: { session: currentSession }} = await supabaseClient.auth.getSession();

        if (!currentSession?.user) {
          setError('Failed to establish session');
          return;
        }

        // Update user metadata
        const { error: updateError } = await supabaseClient.auth.updateUser({
          data: { forceResetPassword: true }
        });

        if (updateError) {
          setError('Failed to update user settings');
          return;
        }

        setIsVerified(true);
        navigate(Routes.RESET_PASSWORD, { replace: true });

      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    if (!isVerified) {
      void verifyInvite();
    }
  }, [navigate, searchParams, supabaseClient, isVerified]);

  return (
    <BrandCardSideContent>
      <div className="w-full max-w-[428px] flex flex-col gap-4">
        {isLoading ? (
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            <p className="text-sm sm:text-base">{t('verifyInvitePage.verifying')}</p>
          </div>
        ) : error ? (
          <div className="flex flex-col gap-4">
            <Alert status="error">{error}</Alert>
            {error.includes('expired') && (
              <p className="text-sm text-center text-gray-600">
                Please contact your organization administrator to resend your invitation.
              </p>
            )}
          </div>
        ) : isVerified ? (
          <div className="flex flex-col items-center gap-4">
            <Alert status="success">Email verified successfully!</Alert>
            <p className="text-sm sm:text-base">Redirecting to password reset...</p>
          </div>
        ) : null}
      </div>
    </BrandCardSideContent>
  );
};

export default VerifyInvitePage;